import type * as React from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import CollectionPreferences, {
  type CollectionPreferencesProps,
} from '@cloudscape-design/components/collection-preferences';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Pagination from '@cloudscape-design/components/pagination';
import PropertyFilter, {
  type PropertyFilterProps,
} from '@cloudscape-design/components/property-filter';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import type { Tenant } from '@etiquetachef/types';
import { countryFlag, formatDate } from '@/lib/format';
import { StatusBadge } from './StatusBadge';
import { TenantDisplayStatusBadge } from './TenantDisplayStatusBadge';
import { useTenantBillingStatuses } from './useTenantBillingStatuses';
import { useTenantFilter } from './tenantFilter';

const PAGE_SIZE_DEFAULT = 25;

const CONTENT_DISPLAY_DEFAULT: readonly { id: string; visible: boolean }[] = [
  { id: 'name', visible: true },
  { id: 'status', visible: true },
  { id: 'billing', visible: true },
  { id: 'kind', visible: true },
  { id: 'country', visible: true },
  { id: 'createdAt', visible: true },
  { id: 'statusChangedAt', visible: true },
];

/** Momento da última transição: `deletedAt` para DELETED, senão `statusChangedAt`. */
function transitionAt(tenant: Tenant): number | undefined {
  return tenant.status === 'DELETED' && tenant.deletedAt
    ? tenant.deletedAt
    : tenant.statusChangedAt;
}

type Props = {
  tenants: Tenant[];
  total: number;
  loading: boolean;
  errorMessage?: string | undefined;
  query: PropertyFilterProps.Query;
  onChangeQuery: (query: PropertyFilterProps.Query) => void;
  currentPageIndex: number;
  pageSize: number;
  onChangePage: (page: number) => void;
  onChangePageSize: (pageSize: number) => void;
  onRefresh: () => void;
  /** Abre o dialog de criação de tenant de cortesia (issue #120). */
  onCreateClick: () => void;
};

/**
 * Tabela da lista de Empresas (`/tenants`): PropertyFilter, paginação e
 * preferências de colunas, com o status comercial por linha.
 *
 * Origem: sem issue — commit baefca8cc, 2026-04-26 16:49 +0200 (Sprint 2);
 * filtro e paginação no servidor desde a #71 (2026-06-21).
 *
 * Contrato: controlada — a página dona (`TenantsPage`) guarda `query`, página
 * e tamanho de página e faz a busca (`useTenantsSearch`); aqui só desenha e
 * devolve as mudanças pelos callbacks. Clique numa linha navega para
 * `/tenants/{id}`.
 *
 * Usado por: `routes/TenantsPage.tsx`. Spec: `docs/screens/tenants-list-admin.md`.
 *
 * Dados: a coluna "Assinatura" lê `billing/subscription` das empresas da
 * página atual (`useTenantBillingStatuses`, domínio Billing) — uma leitura
 * por linha visível, não da coleção inteira.
 *
 * Issues: #71 (busca server-side), #100 (status comercial), #120 (botão de
 * cortesia).
 */
export function TenantList(props: Props): React.JSX.Element {
  const { tenants, total, loading, errorMessage, query, currentPageIndex, pageSize } = props;
  const { onChangeQuery, onChangePage, onChangePageSize, onRefresh, onCreateClick } = props;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const filter = useTenantFilter();
  // Status comercial (Assinatura) por linha — carregado da página atual.
  const billingStatuses = useTenantBillingStatuses(tenants);

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: PAGE_SIZE_DEFAULT,
    wrapLines: false,
    contentDisplay: CONTENT_DISPLAY_DEFAULT,
  });

  const pagesCount = Math.max(1, Math.ceil(total / pageSize));

  const columnDefinitions = useMemo(
    () => [
      {
        id: 'name',
        header: t('tenants.columns.name'),
        cell: (tn: Tenant): React.ReactNode => (
          <div>
            <Link
              href={`/tenants/${tn.id}`}
              onFollow={(e): void => {
                e.preventDefault();
                void navigate(`/tenants/${tn.id}`);
              }}
            >
              {tn.name}
            </Link>
            <Box fontSize="body-s" color="text-status-inactive" fontWeight="normal">
              <CopyToClipboard
                variant="inline"
                textToCopy={tn.id}
                textToDisplay={<span style={{ fontFamily: 'monospace' }}>{tn.id}</span>}
                copyButtonAriaLabel={t('common.copy')}
                copySuccessText={t('common.copied')}
                copyErrorText={t('common.copyError')}
              />
            </Box>
          </div>
        ),
      },
      {
        id: 'status',
        header: t('tenants.columns.status'),
        cell: (tn: Tenant): React.ReactNode => <StatusBadge status={tn.status} />,
      },
      {
        id: 'billing',
        header: t('tenants.columns.billing'),
        cell: (tn: Tenant): React.ReactNode => {
          const r = billingStatuses.get(tn.id);
          return (
            <TenantDisplayStatusBadge
              tenant={tn}
              subscription={r?.kind === 'READY' ? r.data : null}
              billingPending={r === undefined || r.kind === 'LOADING'}
              billingError={r?.kind === 'ERROR'}
            />
          );
        },
      },
      {
        id: 'kind',
        header: t('tenants.columns.kind'),
        cell: (tn: Tenant): React.ReactNode => t(`tenants.kind.${tn.kind}`),
      },
      {
        id: 'country',
        header: t('tenants.columns.country'),
        cell: (tn: Tenant): React.ReactNode =>
          tn.countryCode ? (
            <span>
              <span aria-hidden="true">{countryFlag(tn.countryCode)}</span>{' '}
              <span style={{ fontFamily: 'monospace' }}>{tn.countryCode}</span>
            </span>
          ) : (
            '—'
          ),
      },
      {
        id: 'createdAt',
        header: t('tenants.columns.createdAt'),
        cell: (tn: Tenant): React.ReactNode => formatDate(tn.createdAt, lang),
      },
      {
        id: 'statusChangedAt',
        header: t('tenants.columns.statusChangedAt'),
        cell: (tn: Tenant): React.ReactNode => {
          const ts = transitionAt(tn);
          return ts ? formatDate(ts, lang) : '—';
        },
      },
    ],
    [t, lang, navigate, billingStatuses],
  );

  return (
    <Table<Tenant>
      items={tenants}
      columnDefinitions={columnDefinitions}
      loading={loading}
      loadingText={t('common.loading', { defaultValue: 'Carregando…' })}
      trackBy="id"
      variant="container"
      stickyHeader
      resizableColumns
      wrapLines={preferences.wrapLines ?? false}
      columnDisplay={preferences.contentDisplay ?? CONTENT_DISPLAY_DEFAULT}
      preferences={
        <CollectionPreferences
          title={t('tenants.preferences.title')}
          confirmLabel={t('tenants.preferences.confirm')}
          cancelLabel={t('tenants.preferences.cancel')}
          preferences={preferences}
          onConfirm={({ detail }): void => {
            setPreferences(detail);
            const nextSize = detail.pageSize ?? PAGE_SIZE_DEFAULT;
            if (nextSize !== pageSize) {
              onChangePageSize(nextSize);
              onChangePage(1);
            }
          }}
          pageSizePreference={{
            title: t('tenants.preferences.pageSizeLabel'),
            options: [
              { value: 10, label: '10' },
              { value: 25, label: '25' },
              { value: 50, label: '50' },
            ],
          }}
          wrapLinesPreference={{
            label: t('tenants.preferences.wrapLinesLabel'),
            description: t('tenants.preferences.wrapLinesDescription'),
          }}
          contentDisplayPreference={{
            title: t('tenants.preferences.columnsLabel'),
            options: [
              { id: 'name', label: t('tenants.columns.name'), alwaysVisible: true },
              { id: 'status', label: t('tenants.columns.status') },
              { id: 'billing', label: t('tenants.columns.billing') },
              { id: 'kind', label: t('tenants.columns.kind') },
              { id: 'country', label: t('tenants.columns.country') },
              { id: 'createdAt', label: t('tenants.columns.createdAt') },
              { id: 'statusChangedAt', label: t('tenants.columns.statusChangedAt') },
            ],
          }}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {errorMessage ?? t('tenants.empty')}
        </Box>
      }
      header={
        <Header
          variant="h1"
          counter={`(${String(total)})`}
          description={t('tenants.subtitle')}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button iconName="refresh" ariaLabel={t('common.refresh')} onClick={onRefresh}>
                {t('common.refresh')}
              </Button>
              <Button variant="primary" onClick={onCreateClick}>
                {t('tenants.create.action')}
              </Button>
            </SpaceBetween>
          }
        >
          {t('tenants.title')}
        </Header>
      }
      filter={
        <PropertyFilter
          query={query}
          onChange={({ detail }): void => {
            // Firestore só faz AND entre campos diferentes (issue #71).
            onChangeQuery({ ...detail, operation: 'and' });
          }}
          filteringProperties={filter.filteringProperties}
          filteringOptions={filter.filteringOptions}
          i18nStrings={filter.i18nStrings}
          countText={t('tenants.resultCount', { count: total })}
          expandToViewport
        />
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          pagesCount={pagesCount}
          onChange={({ detail }): void => {
            onChangePage(detail.currentPageIndex);
          }}
        />
      }
    />
  );
}
